/**
 * Priority call-off lines.
 *
 * CR-8613: the customer ticks Priority on a warehouse line in the New Call Off
 * wizard (Add Warehouse Delivery), and the tick belongs to that call-off alone.
 * It was a part's flag per supplier, ticked in Stock Planning (CR-7744); that
 * tick has moved here. The line passes the flag to the supplier's delivery
 * lines, and every screen that lists them marks it the same way: an orange bar
 * down the row's first cell and a Priority badge. The wizard grid also fills
 * the row orange, unless a Stock Health warning needs that background.
 */

/** Row fill for a priority row in the warehouse wizard grid. */
export const PRIORITY_ROW_CLASS = 'bg-orange-100 hover:bg-orange-100';

/**
 * Orange bar down the first cell of a priority row. A cell shadow rather than
 * a row border, because a border on a `<tr>` does not render in a collapsed
 * table. It sits beside any row fill, so it never hides a warning tint.
 */
export const PRIORITY_EDGE_CLASS = 'shadow-[inset_4px_0_0_0_#f97316]';

/** Where priority is set, for tooltips on the screens that only show it. */
export const PRIORITY_SET_ON_CALL_OFF = 'Priority is ticked per call-off line in the warehouse wizard';

/**
 * Tooltip for a Priority badge or row: who ticked the line, and when.
 *
 * @example priorityTitle('Ada Admin', '2026-09-29T14:05:00Z', formatDateTime)
 *   // → 'Priority — set by Ada Admin, 29/09/2026 14:05'
 * @param setByName - who ticked it
 * @param setAt - when it was ticked (ISO string)
 * @param formatDateTime - the user's date-time formatter
 * @returns the tooltip text
 */
export function priorityTitle(
  setByName: string | null | undefined,
  setAt: string | null | undefined,
  formatDateTime: (date: string) => string,
): string {
  const who = setByName ? ` by ${setByName}` : '';
  const when = setAt ? `, ${formatDateTime(setAt)}` : '';
  return who || when ? `Priority — set${who}${when}` : 'Priority';
}

/**
 * Priority rows first, everything else after, each group in its given order.
 *
 * @param rows - the rows to order
 * @param isPriority - reads a row's flag
 * @returns a new array; `rows` is not changed
 */
export function priorityFirst<T>(rows: readonly T[], isPriority: (row: T) => boolean): T[] {
  return [...rows.filter(isPriority), ...rows.filter(row => !isPriority(row))];
}
